customElements.component = 'video-preview-slider';

if (!customElements.get(customElements.component)) {
  class VideoPreviewSlider extends HTMLElement {
    constructor() {
      super();
      this.previewDuration = 5;
      this.debounceTimer = null;
      this.isAutoplayPaused = false;
      this.shouldCycleAtEnd = false;
      this.visibleSlidesToCycle = null;
      this.currentCycleIndex = 0;
      this.onlyVideoPreview = this.hasAttribute('data-only-video-preview');
    }

    connectedCallback() {
      this.shouldCycleAtEnd = this.hasAttribute('data-cycle-at-end');
      this.setupModalBlurHandler();
      this.setupModalPauseHandler();
      this.initializeWhenReady();
    }

    getPrefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    setupModalBlurHandler() {
      this.addEventListener(
        'pointerdown',
        () => {
          const activeElement = document.activeElement;

          if (
            activeElement instanceof HTMLElement &&
            this.contains(activeElement) &&
            activeElement.matches('.video-modal__open-modal')
          ) {
            activeElement.blur();
          }
        },
        {capture: true},
      );
    }

    setupModalPauseHandler() {
      document.addEventListener('video-modal:open', () => {
        this.pausedVideosByModal = !this.isAutoplayPaused;
        this.wasCyclingBeforeModal = this.visibleSlidesToCycle !== null;
        if (this.wasCyclingBeforeModal) {
          this.savedCycleIndex = this.currentCycleIndex;
        }
        if (this.pausedVideosByModal) {
          this.stopAllVideoPreviews();
        }
      });

      document.addEventListener('video-modal:close', () => {
        if (this.pausedVideosByModal) {
          this.pausedVideosByModal = false;
          if (this.wasCyclingBeforeModal) {
            if (this.savedCycleIndex !== undefined) {
              this.currentCycleIndex = this.savedCycleIndex;
            }
            this.startVisibleSlidesCycle();
            this.savedCycleIndex = undefined;
          } else {
            this.playActiveSlidePreview();
          }
        }
      });
    }

    initializeWhenReady() {
      customElements.whenDefined('swiperjs-slider').then(() => {
        const swiperElement = this.querySelector('swiperjs-slider');
        const slideChangeEvent = swiperElement?.dataset.slideChangeEvent;

        if (!slideChangeEvent) return;

        this.addEventListener(slideChangeEvent, (e) => {
          this.handleSlideChange(e);
        });

        if (this.getPrefersReducedMotion()) {
          this.isAutoplayPaused = true;
        }

        this.setupPlayStopButton();

        if (!this.isAutoplayPaused) {
          requestAnimationFrame(() => {
            this.checkInitialState();
            this.playActiveSlidePreview();
          });
        }
      });
    }

    checkInitialState() {
      if (!this.shouldCycleAtEnd) return;
      
      const swiperElement = this.querySelector('swiperjs-slider');
      if (!swiperElement) return;

      const swiperInstance = swiperElement.swiper;
      if (!swiperInstance) return;

      if (swiperInstance.isLocked || swiperInstance.isEnd) {
        swiperElement.setAttribute('data-at-end', '');
      }
    }

    handleSlideChange(event) {
      if (this.isAutoplayPaused) return;

      const swiperElement = this.querySelector('swiperjs-slider');
      const isAtEnd = swiperElement?.hasAttribute('data-at-end');

      if (this.visibleSlidesToCycle && !isAtEnd) {
        this.visibleSlidesToCycle = null;
      }

      if (this.visibleSlidesToCycle) return;

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.playActiveSlidePreview(event.detail?.activeSlide);
      }, 150);
    }

    setupPlayStopButton() {
      const button = this.querySelector('[data-ugc-play-stop]');
      if (!button) return;

      if (this.isAutoplayPaused) {
        button.dataset.ugcPlaying = 'false';
        button.setAttribute('aria-pressed', 'true');
      }

      button.addEventListener('click', () => {
        this.isAutoplayPaused = !this.isAutoplayPaused;

        if (this.isAutoplayPaused) {
          this.pauseAutoplay(button);
        } else {
          this.resumeAutoplay(button);
        }
      });
    }

    pauseAutoplay(button) {
      button.dataset.ugcPlaying = 'false';
      button.setAttribute('aria-pressed', 'true');
      this.visibleSlidesToCycle = null;
      this.stopAllVideoPreviews();
    }

    resumeAutoplay(button) {
      button.dataset.ugcPlaying = 'true';
      button.setAttribute('aria-pressed', 'false');
      this.playActiveSlidePreview();
    }

    stopAllVideoPreviews() {
      this.querySelectorAll('.ugc-video__preview-video').forEach((video) => {
        if (video.timeUpdateHandler) {
          video.removeEventListener('timeupdate', video.timeUpdateHandler);
          video.timeUpdateHandler = null;
        }
        video.loop = false;
        video.removeAttribute('src');
        video.load();
      });
    }

    playSingleSlidePreview(slide) {
      this.stopAllVideoPreviews();
      const video = slide.querySelector('.ugc-video__preview-video[data-src]');
      if (!video) return;

      this.loadVideoSource(video);
      this.setupVideoLoop(video);
      this.playVideo(video);
    }

    loadVideoSource(video) {
      if (video.getAttribute('src') !== video.dataset.src) {
        video.src = video.dataset.src;
      }
      video.loop = this.onlyVideoPreview ? false : true;
    }

    setupVideoLoop(video) {
      const loopHandler = () => {
        if (video.currentTime >= this.previewDuration) {
          if (this.onlyVideoPreview) {
            video.removeEventListener('timeupdate', loopHandler);
            video.timeUpdateHandler = null;
            video.pause();
          } else {
            video.currentTime = 0;
          }
        }
      };
      video.timeUpdateHandler = loopHandler;
      video.addEventListener('timeupdate', loopHandler);
    }

    setupVideoCycleHandler(video, slides) {
      video.startTime = Date.now();

      const cycleHandler = () => {
        const elapsedTime = (Date.now() - video.startTime) / 1000;
        if (elapsedTime >= this.previewDuration) {
          this.currentCycleIndex = (this.currentCycleIndex + 1) % slides.length;
          this.playNextSlideInCycle();
        }
      };

      video.timeUpdateHandler = cycleHandler;
      video.addEventListener('timeupdate', cycleHandler);
    }

    playVideo(video) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }

    startVisibleSlidesCycle() {
      const visibleSlides = this.getVisibleSlidesWithVideos();

      if (!visibleSlides.length) {
        const activeSlide = this.querySelector('.swiper-slide-active');
        if (activeSlide) this.playSingleSlidePreview(activeSlide);
        return;
      }

      this.visibleSlidesToCycle = visibleSlides;
      this.continueOrStartCycle(visibleSlides);
    }

    getVisibleSlidesWithVideos() {
      return [...this.querySelectorAll('.swiper-slide-fully-visible')].filter(
        (slide) => slide.querySelector('.ugc-video__preview-video[data-src]'),
      );
    }

    continueOrStartCycle(slides) {
      const currentlyPlayingVideo = this.querySelector(
        '.ugc-video__preview-video[src]',
      );
      const playingSlide = currentlyPlayingVideo?.closest('.swiper-slide');
      const playingSlideIndex = playingSlide
        ? slides.indexOf(playingSlide)
        : -1;

      if (playingSlideIndex !== -1) {
        this.currentCycleIndex = playingSlideIndex;
        this.updatePlayingVideoToCycle(currentlyPlayingVideo, slides);
      } else {
        if (this.savedCycleIndex === undefined) {
          this.currentCycleIndex = 0;
        }
        this.playNextSlideInCycle();
      }
    }

    updatePlayingVideoToCycle(video, slides) {
      if (video.timeUpdateHandler) {
        video.removeEventListener('timeupdate', video.timeUpdateHandler);
      }
      this.setupVideoCycleHandler(video, slides);
    }

    playNextSlideInCycle() {
      const slides = this.visibleSlidesToCycle;
      if (!slides?.length) return;

      const slide = slides[this.currentCycleIndex % slides.length];
      this.stopAllVideoPreviews();

      const video = slide.querySelector('.ugc-video__preview-video[data-src]');
      if (!video) return;

      this.loadVideoSource(video);
      this.setupVideoCycleHandler(video, slides);
      this.playVideo(video);
    }

    playActiveSlidePreview(activeSlide) {
      if (this.isAutoplayPaused) return;
      if (this.visibleSlidesToCycle) return;

      if (this.shouldCycleAtEnd) {
        const swiperElement = this.querySelector('swiperjs-slider');
        if (swiperElement?.hasAttribute('data-at-end')) {
          this.startVisibleSlidesCycle();
          return;
        }
      }

      activeSlide = activeSlide ?? this.querySelector('.swiper-slide-active');
      if (!activeSlide) return;
      this.playSingleSlidePreview(activeSlide);
    }
  }

  customElements.define(customElements.component, VideoPreviewSlider);
}
